import { evaluateConditions, instanceCountKey, type FieldValue } from '@bmo/form-core';

import type { WorkflowStep } from '@/api/workflows';

type Conditional = {
  conditions?: { fieldId: string }[] | null;
  conditionCombinator?: 'all' | 'any' | null;
};

/**
 * `clearHiddenAnswers` (opt-in, QA TFSA D05/D15/D16): when the answer to a choice field hides a
 * step, section or field whose OWN conditions name that field, the hidden answers are cleared — so
 * switching back to "Yes" shows them empty instead of bringing the old ones back. A repeatable
 * section's instance answers (`inst:<sectionId>:…`) go with it, and so does its instance COUNT, so
 * it returns at its floor rather than at the same number of blank instances. Off (the default),
 * hidden answers are kept and only left out of the review and the PDF, as before.
 *
 * `values` already holds the new answer; returns it untouched when nothing is hidden.
 */
export function clearAnswersHiddenBy(
  steps: readonly WorkflowStep[],
  fieldId: string,
  values: Record<string, FieldValue>,
): Record<string, FieldValue> {
  const controller = steps
    .flatMap((s) => s.sections ?? [])
    .flatMap((sec) => sec.rows)
    .flatMap((r) => r.columns)
    .find((c) => c.field?.id === fieldId)?.field;
  if (!controller?.clearHiddenAnswers) return values;

  // Hidden BY this field: its conditions name it, and they no longer hold.
  const hiddenByIt = (x: Conditional) =>
    (x.conditions ?? []).some((c) => c.fieldId === fieldId) &&
    !evaluateConditions(x.conditions as never, x.conditionCombinator, values);

  const fieldIds = new Set<string>();
  const sectionIds = new Set<string>();
  for (const step of steps) {
    const stepHidden = hiddenByIt(step as Conditional);
    for (const section of step.sections ?? []) {
      const sectionHidden = stepHidden || hiddenByIt(section as Conditional);
      if (sectionHidden) sectionIds.add(section.id);
      for (const row of section.rows)
        for (const col of row.columns) {
          const f = col.field;
          if (!f || f.id === fieldId) continue;
          if (sectionHidden || hiddenByIt(f as Conditional)) fieldIds.add(f.id);
        }
    }
  }

  // The count sentinel goes with the `inst:` keys it counted. Left behind it outlived
  // them: a section hidden holding three instances came back holding three EMPTY ones,
  // and the advisor had to delete two by hand to get the clean block the clear promised.
  // Dropping it is the whole reset — `getInstanceCount` falls back to the section's floor
  // once neither the sentinel nor an `inst:` key remains, so there are no defaults to
  // re-apply here. A mirrored section (`repeatWithSectionId`) whose
  // source stays visible is re-synced by `syncMirroredInstanceCounts`, so dropping its
  // sentinel cannot strand it below its source.
  const sentinels = new Set([...sectionIds].map(instanceCountKey));

  let changed = false;
  const next: Record<string, FieldValue> = {};
  for (const [k, v] of Object.entries(values)) {
    const inst = /^inst:([^:]+):/.exec(k);
    if (fieldIds.has(k) || sentinels.has(k) || (inst && sectionIds.has(inst[1]!))) {
      changed = true;
      continue;
    }
    next[k] = v;
  }
  return changed ? next : values;
}
